import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import Card from "../../../../../components/layouts/card/card";
import FormFieldLineLayout from "../../../../../components/layouts/form-field-line-layout/form-field-line-layout";
import TranslationInput from "../../../../../translation/translation-input/translation-input";
import TranslationTextarea from "../../../../../translation/translation-textarea/translation-textarea";
import { RETURN_METHOD_TYPE, SLIP_BARCODE_TYPE, ShippingInstructionsChangeTrigger, CREATE_RETURN_IN_INTEGRATION_TYPE, slipBarCodeTypes } from "../../../return-method-types";
import {  RETURN_METHOD_TYPES_MAP } from "../../../constants";
import ReturnMethodStore from "../../../stores/return-method-store"
import { observer } from "mobx-react";
import DropOffLocationsField from "./drop-off-locations-field/drop-off-locations-field";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { FEATURES } from "../../../../../models/Plan";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import Icon from "../../../../../components/icons/icon/icon";
import IconOnOffFeatures from "../../../../../components/icons/icon-on-off-features/icon-on-off-features";
import "./details-card.scss";
import { Input } from "../../../../../components/inputs/input/input";
import useTranslation from "../../../../../translation/hooks/translation-hook";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import { InstrucionsWarningFieldLine } from "../instructions-warning";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import { toJS } from "mobx";

interface Props {
    returnMethod?: ReturnMethodStore;
    validationGroup: IValidationGroup;
}
const DetailsCard: React.FC<Props> = observer(({returnMethod, validationGroup}: Props) => {

    const { setTranslation, getTranslation} = useTranslation();
    const { getTranslation: getDashboardTranslation } = useDashboardTranslation();
    const scope = "dashboard-return-method-editor";


    const shipWithAnyCarrierCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_ANY_CARRIER);
    const returnToDropOffLocationCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION);
    const noShipmentRequiredCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_NO_SHIPMENT_REQUIRED);
    const shipWithPrepaidLabelCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL);

    const populateReturnMethodTypesList = (
        getTranslation: (key: string, scope: string) => string,
    ) => {
        const isSupportedByType: Record<RETURN_METHOD_TYPE, boolean> = {
            [RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED]: noShipmentRequiredCapability.isSupported,
            [RETURN_METHOD_TYPE.DROP_OFF_LOCATION]: returnToDropOffLocationCapability.isSupported,
            [RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER]: shipWithAnyCarrierCapability.isSupported,
            [RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL]: shipWithPrepaidLabelCapability.isSupported,
        };

        const options = Object.keys(RETURN_METHOD_TYPES_MAP)
            .map(k => RETURN_METHOD_TYPES_MAP[(k as unknown) as RETURN_METHOD_TYPE])
            .filter(option => isSupportedByType[option.value]);

        const translatedOptions = options.map(option => ({
            value: option.value,
            label: getTranslation(option.label, 'dashboard-return-method-editor')
        }));

        return translatedOptions;
    }

    const isShipWithAnyCarrier = returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER;
    const isDropOffLocation = returnMethod?.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION;
    const isNoShipmentRequired = returnMethod?.methodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED;
    const isShipWithPrepaidLabel = returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL;

    if (!returnMethod) {
        return null;
    }

    const packingSlipAllowed = currentPortalStore.isFeatureAllowed(FEATURES.PACKING_SLIP);
    const slipBarCodeTypesOptions = slipBarCodeTypes(getDashboardTranslation);
    const packingSlipSelectedSlipBarcodeType = slipBarCodeTypesOptions.find(op => op.value === returnMethod.packingSlipBarcodeType);
    const addressSlipSelectedSlipBarcodeType = slipBarCodeTypesOptions.find(op => op.value === returnMethod.addressSlipBarcodeType);
    const returnMethodTypesList = populateReturnMethodTypesList(getDashboardTranslation);
    const selectedReturnMethodType = returnMethodTypesList.find(op => op.value === returnMethod.methodType);

    return(
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getDashboardTranslation("DETAILS", scope)}</FontBody1>,
            }}
        >
            <FormFieldLineLayout>

                {(!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isInternalNameSupported) &&
                 (!isDropOffLocation || returnToDropOffLocationCapability.isInternalNameSupported) &&
                 (!isNoShipmentRequired || noShipmentRequiredCapability.isInternalNameSupported) &&
                 (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isInternalNameSupported) && (
                    <FormFieldLine
                        label={getDashboardTranslation("INTERNAL_NAME", scope)}
                        note={getDashboardTranslation("INTERNAL_NAME_NOTE", scope)}
                        required
                    >
                        <Input
                        /** since this is an internal name, we have an old mechanism that saved all the internal names only in en locale.
                         * we decided to keep it as is, althogh the merchant can write in any other language as well */
                            value={getTranslation(returnMethod.name, "en")}
                            onChange={(v) => setTranslation(returnMethod.name, v, "en")}
                            required
                            validationGroup={validationGroup}
                        />
                    </FormFieldLine>
                )}

                {(!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isDisplayNameSupported) &&
                 (!isDropOffLocation || returnToDropOffLocationCapability.isDisplayNameSupported) &&
                 (!isNoShipmentRequired || noShipmentRequiredCapability.isDisplayNameSupported) &&
                 (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isDisplayNameSupported) && (
                    <FormFieldLine
                        label={getDashboardTranslation("DISPLAY_NAME", scope)}
                        note={getDashboardTranslation("DISPLAY_NAME_NOTE", scope)}
                        required
                    >
                        <TranslationInput translationKey={returnMethod.displayName} required validationGroup={validationGroup}/>
                    </FormFieldLine>
                )}

                {(!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isDisplayDescriptionSupported) &&
                 (!isDropOffLocation || returnToDropOffLocationCapability.isDisplayDescriptionSupported) &&
                 (!isNoShipmentRequired || noShipmentRequiredCapability.isDisplayDescriptionSupported) &&
                 (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isDisplayDescriptionSupported) && (
                    <FormFieldLine
                        label={getDashboardTranslation("DISPLAY_DESCRIPTION", scope)}
                        note={getDashboardTranslation("DISPLAY_DESCRIPTION_NOTE", scope)}
                    >
                        <TranslationTextarea
                            translationKey={returnMethod?.description}
                        />
                    </FormFieldLine>
                )}

	            {returnMethodTypesList.length > 1 && (
					<>
						<FormFieldLine
							label={getDashboardTranslation("RETURN_METHOD_TYPE", scope)}
						>
							<SelectDropdown
								value={selectedReturnMethodType}
								options= {returnMethodTypesList}
								onChange={v => returnMethod.setMethodType(v?.value as RETURN_METHOD_TYPE)}
							/>
						</FormFieldLine>
						<InstrucionsWarningFieldLine returnMethod={returnMethod} trigger={ShippingInstructionsChangeTrigger.RETURN_METHODD_TYPE} />
					</>
	            )}

                {returnMethod.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION &&
                    <DropOffLocationsField returnMethod={returnMethod} validationGroup={validationGroup}/>
                }
                { !!returnMethod.showPackingSlipToggle() && (!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isIncludePackingSlipSupported) &&
                    (!isDropOffLocation || returnToDropOffLocationCapability.isIncludePackingSlipSupported) && (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isIncludePackingSlipSupported) &&
                    <div className="packing-slip-block">
                        <FontBody2 color={FONT_COLOR.PRIMARY2}>{getDashboardTranslation("INCLUDE_A_PACKING_SLIP", scope)}</FontBody2>
                        <div className="toggle-block">
                            <Toggle
                                label=""
                                checked={returnMethod.hasPackingSlip}
                                onChange={v => returnMethod.setHasPackingSlip(v)}
                                disabled={!packingSlipAllowed}
                            />
                            { !packingSlipAllowed &&
                                <Tooltip content={getDashboardTranslation("FEATURE_NOT_INCLUDED", scope)}>
                                    <Icon Component={IconOnOffFeatures} />
                                </Tooltip>
                            }
                        </div>
                    </div>
                }

                { returnMethod.showPackingSlipToggle() && returnMethod.hasPackingSlip &&
                    (!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isIncludePackingSlipSupported) &&
                    (!isDropOffLocation || returnToDropOffLocationCapability.isIncludePackingSlipSupported) &&
                    (!isShipWithPrepaidLabel || shipWithPrepaidLabelCapability.isIncludePackingSlipSupported) &&
                    <FormFieldLine label={getDashboardTranslation("PACKING_SLIP_BARCODE_TYPE", scope)}>
                        <SelectDropdown
                            value={packingSlipSelectedSlipBarcodeType}
                            options={slipBarCodeTypesOptions}
                            onChange={(v) => {
                                if (v) {
                                    returnMethod?.setPackingSlipBarcodeType(v.value as SLIP_BARCODE_TYPE);
                                }
                            }}
                            disabled={!packingSlipAllowed}
                        />
                    </FormFieldLine>
                }

                { !!returnMethod.showAddressSlipToggle() &&
                    (!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isIncludeAddressSlipSupported) &&
                    <FormFieldLine label={getDashboardTranslation("INCLUDE_AN_ADDRESS_SLIP", scope)}>
                        <Toggle
                            label=""
                            checked={returnMethod.hasAddressSlip}
                            onChange={v => returnMethod.setHasAddressSlip(v)}
                        />
                    </FormFieldLine>
                }

                { returnMethod.showPackingSlipToggle() && returnMethod.hasAddressSlip &&
                    (!isShipWithAnyCarrier || shipWithAnyCarrierCapability.isIncludeAddressSlipSupported) &&
                    <FormFieldLine label={getDashboardTranslation("ADDRESS_SLIP_BARCODE_TYPE", scope)}>
                        <SelectDropdown
                            value={addressSlipSelectedSlipBarcodeType}
                            options={slipBarCodeTypesOptions}
                            onChange={(v) => {
                                if (v) {
                                    returnMethod?.setAddressSlipBarcodeType(v.value as SLIP_BARCODE_TYPE);
                                }
                            }}
                            disabled={!packingSlipAllowed}
                        />
                    </FormFieldLine>
                }

                {returnMethod.hasInventoryTrackingConnections && returnMethod.methodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED &&
                    <FormFieldLine
                        label={getDashboardTranslation("UPDATE_WMS_3PL_ERP", scope)}
                        required={true}
                    >
                        <SelectDropdown
                            value={returnMethod?.getSelectedCreateReturnInWmsOption(getDashboardTranslation)}
                            options={returnMethod?.getCreateReturnInWmsOptionsList(getDashboardTranslation) ?? []}
                            onChange={v => returnMethod?.setCreateReturnInWms(v?.value as CREATE_RETURN_IN_INTEGRATION_TYPE | number)}
                            required
                            validationGroup={validationGroup}
                        />
                    </FormFieldLine>
                }

            </FormFieldLineLayout>
        </Card>
    )
});

export default DetailsCard;
